import React, { useState, useEffect } from 'react'
import { Auth } from 'aws-amplify'
import { Badge, Button, Input, Select } from 'antd'
import PhoneInput from 'react-phone-number-input'
import {
    setShowPopUp,
    showConfirmationForm,
    setShowHelp,
    setShowNotifications,
} from '../../../redux/auth/actions'
import {
    comparePasswords,
    validateEmail,
    validatePassword,
    validatePhoneNumber
} from '../helpers'
import '../../../styles/PhoneInput.css'
import NotificationIcon from '../../../icons/Notifications'
import QuestionIcon from '../../../icons/Question'
import { validateExtraField } from '../../../lib/stringOperations'

const { Option } = Select

const styles = {
    button: {
        backgroundColor: '#1BAAF1',
        borderColor: '#1BAAF1',
    },
    splitColumns: {
        marginTop: '0.64em',
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
    },
    buttonContainer: {
        marginTop: '1em',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
    },
    submitButton: {
        width: 180,
        height: 40,
        borderRadius: 7,
    },
    required: {
        color: 'red',
        marginRight: 5,
    },
    error: {
        border: '1px solid red',
    },
    errorText: {
        display: 'block',
        color: 'red',
        marginTop: '0.64em'
    }
}

const RegistrationForm = ({ data }) => {
    const suitesEnabled = data.building.settings?.systemConfig?.suites

    const [formValues, setFormValues] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        floor: '',
        suite: '',
        workPhone: '',
        extension: '',
        phone: '',
        extraFields: {},
    })
    const [errorFields, setErrorFields] = useState({})
    const [errorMessage, setErrorMessage] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (Object.keys(errorFields).length > 0) {
            const msgArray = []
            for (const key in errorFields) {
                if (typeof errorFields[key] === 'string') {
                    msgArray.push(`${key}: ${errorFields[key]}`)
                } else if (typeof errorFields[key] === 'boolean' && msgArray.indexOf(`Please fill out all required fields`) === -1) {
                    msgArray.push(`Please fill out all required fields`)
                }
            }
            setErrorMessage(msgArray)
        } else {
            setErrorMessage([])
        }

    }, [errorFields])

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }))
    }

    const handleSelectChange = (name, value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }))
    }

    const onExtraFieldsChange = (name, value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            extraFields: {
                ...prevValues.extraFields,
                [name]: value,
            },
        }))
    }

    const validateFields = () => {
        const errors = {}

        if (!validateEmail(formValues.email)) errors.email = true
        if (!formValues.firstName) errors.firstName = true
        if (!validatePassword(formValues.password)) errors.password = "Passwords must be at least 8 characters and include at least one number, one uppercase letter, one lowercase letter, and one special character."
        if (!comparePasswords(formValues.password, formValues.confirmPassword)) errors.confirmPassword = "Passwords don't match"
        if (!formValues.lastName) errors.lastName = true
        if (!formValues.floor) errors.floor = true
        if (suitesEnabled && !formValues.suite) errors.suite = true

        if (!validatePhoneNumber(formValues.workPhone, data.settings?.workPhone)) errors.workPhone = "Input Valid Phone number"
        if (!validatePhoneNumber(formValues.phone, data.settings?.phone)) errors.phone = "Input Valid Phone number"


        if (data.settings?.fields) {

            data.settings.fields.forEach((field) => {

                const value = formValues.extraFields[field.label]
                if (field) {
                    const isValid = validateExtraField(
                        value,
                        field.required,
                        field.advanced.numerical,
                        field.advanced.minLength
                    )
                    if (!isValid) errors[field.label] = generateExtraFieldToolTip(field)
                }
            })
        }

        setErrorFields(errors)
        return Object.keys(errors).length > 0
    }

    const generateExtraFieldToolTip = (field) => {
        let str = ''

        if (field.advanced.numerical) {
            str = str + 'Only Numbers.'
        }

        if (field.advanced.minLength) {
            str = str + ` A minimum of ${field.advanced.minLength} characters.`
        }

        return str ? str : false
    }

    const handleSubmit = () => {
        if (isSubmitting) return
        if (!validateFields()) {
            const attributes = {
                email: formValues.email.toLowerCase(),
                given_name: formValues.firstName,
                family_name: formValues.lastName,
                'custom:d1': `${data.tenant.d1}#${data.tenant.pk}`,
                'custom:floor': formValues.floor,
                ...(formValues.suite && { 'custom:suite': formValues.suite }),
                ...(formValues.workPhone && { 'custom:work_landline': formValues.workPhone }),
                ...(formValues.phone && { phone_number: formValues.phone }),
                ...(formValues.extension && { 'custom:phone_extension': formValues.extension })
            }

            if (formValues.extraFields) {
                Object.keys(formValues.extraFields).forEach((key, index) => {
                    if (formValues.extraFields[key] !== undefined) {
                        attributes[`custom:field${index + 1}`] = `${key}#${formValues.extraFields[key]}`
                    }
                })
            }

            setIsSubmitting(true)
            Auth.signUp({
                username: attributes.email,
                password: formValues.password,
                attributes,
            })
                .then(() => {
                    showConfirmationForm(attributes.email)
                })
                .catch((err) => {
                    setIsSubmitting(false)
                    if (err.code === 'UsernameExistsException') {
                        setShowPopUp(true)
                    } else {
                        setErrorMessage(err.message)
                    }
                })
        }
    }

    if (!data) return null

    return (
        <>
            <div style={styles.errorText}>
                <ul>
                    {errorMessage.map((msg, index) => (
                        <li key={index}>{msg}</li>
                    ))}
                </ul>
            </div>
            <div style={{ marginTop: '1em' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ width: 200 }}>
                        <span style={styles.required}>*</span>
                        <span style={{ width: 150 }}>First Name:</span>
                    </div>
                    <div style={{ width: 60, display: 'flex', justifyContent: 'space-around', padding: 2 }}>
                        <span style={{ cursor: 'pointer' }} onClick={() => setShowHelp(true)}>
                            <QuestionIcon height={20} />
                        </span>
                        <span style={{ cursor: 'pointer' }} onClick={() => setShowNotifications(true)}>
                            <Badge dot>
                                <NotificationIcon height={20} />
                            </Badge>
                        </span>
                    </div>
                </div>
                <Input
                    name="firstName"
                    style={errorFields.firstName ? styles.error : null}
                    onChange={handleChange}
                    value={formValues.firstName}
                />
            </div>
            <div style={{ marginTop: '1em' }}>
                <span>
                    <span style={styles.required}>*</span>
                    Last Name:
                </span>
                <Input
                    name="lastName"
                    style={errorFields.lastName ? styles.error : null}
                    onChange={handleChange}
                    value={formValues.lastName}
                />
            </div>
            <div style={{ marginTop: '1em' }}>
                <span>
                    <span style={styles.required}>*</span>
                    Email:
                </span>
                <Input
                    name="email"
                    style={errorFields.email ? styles.error : null}
                    onChange={handleChange}
                    value={formValues.email}
                />
            </div>
            <div style={{ marginTop: '1em' }}>
                <span>
                    <span style={styles.required}>*</span>
                    Password:
                </span>
                <Input
                    name="password"
                    style={errorFields.password ? styles.error : null}
                    onChange={handleChange}
                    type="password"
                    value={formValues.password}
                />
            </div>
            <div style={{ marginTop: 2 }}>
                <Input
                    name="confirmPassword"
                    style={errorFields.confirmPassword ? styles.error : null}
                    onChange={handleChange}
                    type="password"
                    value={formValues.confirmPassword}
                    placeholder="Confirm Password..."
                />
            </div>
            <div style={styles.splitColumns}>
                <div style={suitesEnabled ? { width: '48%' } : { width: '100%' }}>
                    <span>
                        <span style={styles.required}>*</span>
                        Floor:
                    </span>
                    <Select
                        name="floor"
                        style={errorFields.floor ? { ...styles.error, width: '100%' } : { width: '100%' }}
                        value={formValues.floor}
                        onChange={(value) => handleSelectChange('floor', value)}
                    >
                        {data.floors.map((floor) => (
                            <Option key={floor} value={floor}>
                                {floor}
                            </Option>
                        ))}
                    </Select>
                </div>
                {suitesEnabled && <div style={{ width: '48%' }}>
                    <span>
                        <span style={styles.required}>*</span>
                        Suite:
                    </span>
                    <Select
                        name="suite"
                        style={errorFields.suite ? { ...styles.error, width: '100%' } : { width: '100%' }}
                        value={formValues.suite}
                        onChange={(value) => handleSelectChange('suite', value)}
                    >
                        {data.suites && data.suites.map((suite) => (
                            <Option key={suite} value={suite}>
                                {suite}
                            </Option>
                        ))}
                    </Select>
                </div>}
            </div>
            <div style={styles.splitColumns}>
                <div style={{ width: '68%' }}>
                    <span>
                        {data.settings?.workPhone && <span style={styles.required}>*</span>}
                        Work Phone:
                    </span>
                    <PhoneInput
                        name="workPhone"
                        style={errorFields.workPhone ? styles.error : null}
                        onChange={(value) => handleSelectChange('workPhone', value)}
                        defaultCountry="US"
                        value={formValues.workPhone}
                    />
                </div>
                <div style={{ width: '28%' }}>
                    <span>Ext:</span>
                    <Input
                        name="extension"
                        onChange={handleChange}
                        value={formValues.extension}
                    />
                </div>
            </div>
            <div style={{ marginTop: '1em', width: '68%' }}>
                <span>
                    {data.settings?.phone && <span style={styles.required}>*</span>}
                    Mobile Phone:
                </span>
                <PhoneInput
                    name="phone"
                    style={errorFields.phone ? styles.error : null}
                    onChange={(value) => handleSelectChange('phone', value)}
                    defaultCountry="US"
                    value={formValues.phone}
                />
            </div>

            {data.settings?.fields?.map((field) => (
                <div key={field.label} style={{ marginTop: '1em' }}>
                    <span>
                        {field.required && <span style={styles.required}>*</span>}
                        {field.label}:
                    </span>
                    <Input
                        name={field.label}
                        style={errorFields[field.label] ? styles.error : null}
                        placeholder={`Input ${field.label}`}
                        value={formValues.extraFields[field.label] || ''}
                        onChange={(e) => onExtraFieldsChange(field.label, e.target.value)}
                    />
                </div>
            ))}

            <div style={styles.buttonContainer}>
                <Button
                    onClick={handleSubmit}
                    style={styles.submitButton}
                    type="primary"
                    htmlType="submit"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    Register
                </Button>
            </div>
        </>
    )
}

export default RegistrationForm
